import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { deleteSession, cancelSession } from "../../services/adminTimeTableServices";
import { generateSessionZoomLink } from "../../services/sessionBookingService";
import SessionModal from "./SessionModal";
import ChangeSessionTimeModal from "./ChangeSessionTimeModal";
import { utcToLocalTime } from "../../../utils/dateTimeUtils";

const SessionTable = ({ dayId, dayDate, sessions, reload }) => {
  const [modalOpen, setModalOpen] = useState(false);
  const [selectedSession, setSelectedSession] = useState(null);
  const [timeModalOpen, setTimeModalOpen] = useState(false);
  const [selectedTimeSession, setSelectedTimeSession] = useState(null);
  const [loadingZoom, setLoadingZoom] = useState({});
  const [loadingCancel, setLoadingCancel] = useState({});
  const [localZoomLinks, setLocalZoomLinks] = useState({});

  const navigate = useNavigate();

  /* ===============================
     Helpers
  =============================== */
  const isSessionExpired = (endTime) => endTime && new Date(endTime) < new Date();

  const getZoomLink = (session) => {

    const link =
    localZoomLinks[session.id] ||
    session.zoomLink ||      // fallback
    session.zoom_link;       // ✅ backend DTO field


    return link;
  };

  const renderZoomStatus = (session) => {
    const zoomLink = getZoomLink(session);

    if (session.isCancelled === true) return <span style={badgeCancelled}>Cancelled</span>;
    if (isSessionExpired(session.endTime)) return <span style={badgeExpired}>Expired</span>;
    if (zoomLink && zoomLink !== "null") return <span style={badgeActive}>Generated</span>;
    return <span style={badgePending}>Not Generated</span>;
  };

  /* ===============================
     Agenda Navigation
  =============================== */
  const openAgendaPage = (session) =>
    navigate(`/admin/session_agendas/${session.id}`);

  /* ===============================
     Delete Session
  =============================== */
  const handleDelete = async (id) => {
    if (!window.confirm("Delete this session?")) return;
    try {
      await deleteSession(id);
      reload();
    } catch (err) {
      alert("Failed to delete session");
    }
  };

  /* ===============================
     Cancel Session
  =============================== */
  const handleCancel = async (sessionId) => {
    if (!window.confirm("Cancel this session?")) return;

    setLoadingCancel((prev) => ({ ...prev, [sessionId]: true }));

    try {
      await cancelSession(sessionId);
      alert("Session canceled successfully!");
      reload();
    } catch (err) {
      alert("Failed to cancel session");
    } finally {
      setLoadingCancel((prev) => ({ ...prev, [sessionId]: false }));
    }
  };

  /* ===============================
     Open Change Time Modal
  =============================== */
  const handleOpenChangeTime = (session) => {
    setSelectedTimeSession(session);
    setTimeModalOpen(true);
  };

  /* ===============================
     Zoom Link Generation (✅ FIXED)
  =============================== */
  const handleGenerateZoom = async (sessionId) => {
    setLoadingZoom((prev) => ({ ...prev, [sessionId]: true }));

    try {
      const result = await generateSessionZoomLink(sessionId);

      // ✅ FIX: support both response shapes
      const zoomLink =
        result?.data?.zoomLink || result?.zoomLink;

      const message =
        result?.data?.message || result?.message;

      if (zoomLink) {
        setLocalZoomLinks((prev) => ({
          ...prev,
          [sessionId]: zoomLink,
        }));

        alert("Zoom link is ready!");
      } else {
        alert(message || "Zoom link not available");
      }

      reload(); // keep backend sync
    } catch (err) {
      const msg = err?.response?.data?.message || "Failed to generate Zoom link";
      alert(msg);
    } finally {
      setLoadingZoom((prev) => ({ ...prev, [sessionId]: false }));
    }
  };

  /* ===============================
     Render Table
  =============================== */
  return (
    <div style={{ border: "1px solid #ccc", borderRadius: "8px", padding: "10px", marginTop: "10px" }}>
      <div style={{ display: "flex", justifyContent: "space-between", marginBottom: "10px" }}>
        <h4>Sessions</h4>
        <button onClick={() => { setSelectedSession(null); setModalOpen(true); }} style={addBtn}>
          ➕ Add Session
        </button>
      </div>

      <table style={{ width: "100%", borderCollapse: "collapse" }}>
        <thead>
          <tr style={{ backgroundColor: "#f0f0f0" }}>
            <th>Topic</th>
            <th>Time</th>
            <th>Plan</th>
            <th>Slots</th>
            <th>Speaker</th>
            <th>Zoom</th>
            <th>Status</th>
            <th>Actions</th>
          </tr>
        </thead>
     <tbody>
  {sessions.map((s) => {
    const expired = isSessionExpired(s.endTime);
    const cancelled = s.cancelled === true;
    const zoomLink = getZoomLink(s);

    return (
      <tr key={s.id}>
        <td>{s.topic}</td>
        <td>{utcToLocalTime(s.startTime)} → {utcToLocalTime(s.endTime)}</td>
        <td>{s.planType}</td>
        <td>{s.totalSlots}</td>
        <td>{s.speakerName || "Not Assigned"}</td>

        <td>
          {cancelled ? (
            <button disabled style={{ color: "#F44336" }}>Cancelled</button>
          ) : zoomLink && zoomLink !== "null" ? (
            <a href={zoomLink} target="_blank" rel="noreferrer">Join</a>
          ) : (
            <button
              onClick={() => handleGenerateZoom(s.id)}
              disabled={expired || loadingZoom[s.id]}
            >
              {expired ? "Expired" : loadingZoom[s.id] ? "Generating..." : "Generate"}
            </button>
          )}
        </td>

        <td>{renderZoomStatus(s)}</td>

        <td>
          <button onClick={() => { setSelectedSession(s); setModalOpen(true); }}>✏️</button>
          <button onClick={() => handleDelete(s.id)}>🗑️</button>

          {!cancelled && (
            <>
              <button onClick={() => handleCancel(s.id)} disabled={loadingCancel[s.id]}>
                {loadingCancel[s.id] ? "Cancelling..." : "Cancel"}
              </button>
              <button onClick={() => handleOpenChangeTime(s)}>Change Time</button>
            </>
          )}

          <button onClick={() => openAgendaPage(s)} style={agendaBtn}>📝 Agenda</button>

          <button onClick={() => navigate(`/admin/sessions/${s.id}/customers`)}>
            👥 View More
          </button>
        </td>
      </tr>
    );
  })}
</tbody>

      </table>

      {modalOpen && (
        <SessionModal
          dayId={dayId}
          dayDate={dayDate}
          session={selectedSession}
          onClose={() => setModalOpen(false)}
          reload={reload}
        />
      )}

      {timeModalOpen && selectedTimeSession && (
        <ChangeSessionTimeModal
          session={selectedTimeSession}
          dayDate={dayDate}
          onClose={() => setTimeModalOpen(false)}
          reload={reload}
        />
      )}
    </div>
  );
};

/* ===============================
   Styles
=============================== */
const addBtn = { padding: "5px 10px", backgroundColor: "#4caf50", color: "white", border: "none", borderRadius: "4px" };
const agendaBtn = { padding: "3px 6px", backgroundColor: "#2196F3", color: "white", border: "none", borderRadius: "4px" };
const badgeActive = { padding: "3px 6px", backgroundColor: "#4CAF50", color: "#fff", borderRadius: "4px", fontSize: "12px" };
const badgePending = { padding: "3px 6px", backgroundColor: "#FFC107", color: "#000", borderRadius: "4px", fontSize: "12px" };
const badgeExpired = { padding: "3px 6px", backgroundColor: "#F44336", color: "#fff", borderRadius: "4px", fontSize: "12px" };
const badgeCancelled = { padding: "3px 6px", backgroundColor: "#9E9E9E", color: "#fff", borderRadius: "4px", fontSize: "12px" };

export default SessionTable;
